import { useState, useRef, useEffect, useCallback, useLayoutEffect, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import styles from './ConnectorSelect.module.css';

interface ConnectorOption {
  id: string;
  name: string;
  environment?: string;
}

interface ConnectorSelectProps {
  options: ConnectorOption[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  disabled?: boolean;
  hasError?: boolean;
}

const envClass: Record<string, string> = {
  prod: styles.envProd,
  test: styles.envTest,
  dev: styles.envDev,
};

function EnvBadge({ env }: { env?: string }) {
  if (!env) return null;
  return (
    <span className={`${styles.envBadge} ${envClass[env] ?? styles.envNone}`}>
      {env}
    </span>
  );
}

export default function ConnectorSelect({
  options,
  value,
  onChange,
  placeholder = 'Select connector',
  disabled = false,
  hasError = false,
}: ConnectorSelectProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties | null>(null);

  const selected = options.find(o => o.id === value);

  const updateDropdownPosition = useCallback(() => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const viewportPadding = 8;
    const dropdownGap = 4;
    const maxMenuHeight = 220;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const openUpwards = spaceBelow < 160 && spaceAbove > spaceBelow;
    const availableHeight = Math.max(
      120,
      Math.min(maxMenuHeight, (openUpwards ? spaceAbove : spaceBelow) - dropdownGap)
    );

    setDropdownStyle(openUpwards
      ? {
        position: 'fixed',
        left: rect.left,
        bottom: window.innerHeight - rect.top + dropdownGap,
        width: rect.width,
        maxHeight: availableHeight,
        zIndex: 1000,
      }
      : {
        position: 'fixed',
        top: rect.bottom + dropdownGap,
        left: rect.left,
        width: rect.width,
        maxHeight: availableHeight,
        zIndex: 1000,
      });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const clickedInsideWrapper = !!wrapperRef.current?.contains(target);
      const clickedInsideDropdown = !!dropdownRef.current?.contains(target);

      if (!clickedInsideWrapper && !clickedInsideDropdown) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;

    updateDropdownPosition();

    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);

    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [open, updateDropdownPosition]);

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        className={`${styles.trigger} ${hasError ? styles.triggerError : ''}`}
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        aria-invalid={hasError}
      >
        <span className={styles.triggerLeft}>
          {selected ? (
            <>
              <span className={styles.triggerName}>{selected.name}</span>
              <EnvBadge env={selected.environment} />
            </>
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </span>
        <ChevronDown size={16} className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} />
      </button>

      {open && dropdownStyle && createPortal(
        <div className={styles.dropdown} style={dropdownStyle} ref={dropdownRef}>
          {options.map(opt => (
            <div
              key={opt.id}
              className={`${styles.option} ${opt.id === value ? styles.optionSelected : ''}`}
              onClick={() => { onChange(opt.id); setOpen(false); }}
            >
              <span className={styles.optionName}>{opt.name}</span>
              <EnvBadge env={opt.environment} />
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
